import * as THREE from 'three';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import {
  normalView,
  positionView,
  uniform,
  uv,
  wgslFn,
} from 'three/tsl';
import type { RendererMode } from './Renderer';

export interface EngineCoreOptions {
  length?: number;
  radius?: number;
  segments?: number;
  rendererMode?: RendererMode;
}

export interface EngineCoreHandle {
  mesh: THREE.Mesh;
  material: THREE.Material;
  setIntensity(intensity: number): void;
  setExtension(extension: number): void;
  setCoreColor(color: THREE.Vector3): void;
  setOuterColor(color: THREE.Vector3): void;
  update(dt: number): void;
  dispose(): void;
}

const makeNodeUniform = uniform as any;
const makeNodeUv = uv as any;
const makeWgslFn = wgslFn as any;

interface WebGPUEngineCoreUniforms {
  uTime: { value: number };
  uIntensity: { value: number };
  uExtension: { value: number };
  uCoreColor: { value: THREE.Vector3 };
  uOuterColor: { value: THREE.Vector3 };
}

const VERT = `
  uniform float uTime;
  uniform float uIntensity;
  varying vec2 vUv;
  varying float vDist;
  varying vec3 vViewNormal;
  varying vec3 vViewPos;

  float vhash(vec3 p) {
    return fract(sin(dot(p, vec3(12.9898, 78.233, 37.719))) * 43758.5453);
  }

  void main() {
    vUv = uv;
    vDist = uv.y;

    float t = uTime * (1.6 + uIntensity * 1.2);
    float n1 = vhash(position * 5.7 + vec3(t, 0.0, 0.0));
    float n2 = vhash(position * 11.3 + vec3(0.0, t * 1.7, 0.0));
    float wobbleAmp = 0.22 * vDist * (0.5 + uIntensity);
    vec3 displaced = position;
    displaced.x += (n1 - 0.5) * wobbleAmp;
    displaced.y += (n2 - 0.5) * wobbleAmp;

    vec4 viewPosition = modelViewMatrix * vec4(displaced, 1.0);
    vViewPos = viewPosition.xyz;
    vViewNormal = normalize(normalMatrix * normal);

    gl_Position = projectionMatrix * viewPosition;
  }
`;

const FRAG = `
  uniform float uTime;
  uniform float uIntensity;
  uniform float uExtension;
  uniform vec3 uCoreColor;
  uniform vec3 uOuterColor;
  varying vec2 vUv;
  varying float vDist;
  varying vec3 vViewNormal;
  varying vec3 vViewPos;

  float hash(vec3 p) {
    p = fract(p * 0.3183099 + vec3(0.1, 0.2, 0.3));
    p *= 17.0;
    return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
  }

  float noise(vec3 p) {
    vec3 i = floor(p);
    vec3 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float n000 = hash(i + vec3(0.0, 0.0, 0.0));
    float n100 = hash(i + vec3(1.0, 0.0, 0.0));
    float n010 = hash(i + vec3(0.0, 1.0, 0.0));
    float n110 = hash(i + vec3(1.0, 1.0, 0.0));
    float n001 = hash(i + vec3(0.0, 0.0, 1.0));
    float n101 = hash(i + vec3(1.0, 0.0, 1.0));
    float n011 = hash(i + vec3(0.0, 1.0, 1.0));
    float n111 = hash(i + vec3(1.0, 1.0, 1.0));
    float nx00 = mix(n000, n100, f.x);
    float nx10 = mix(n010, n110, f.x);
    float nx01 = mix(n001, n101, f.x);
    float nx11 = mix(n011, n111, f.x);
    float nxy0 = mix(nx00, nx10, f.y);
    float nxy1 = mix(nx01, nx11, f.y);
    return mix(nxy0, nxy1, f.z);
  }

  float fbm(vec3 p) {
    float v = 0.0;
    float a = 0.5;
    for (int i = 0; i < 4; i++) {
      v += a * noise(p);
      p *= 2.18;
      a *= 0.5;
    }
    return v;
  }

  void main() {
    if (uIntensity <= 0.001) discard;

    float scroll = uTime * (1.8 + uIntensity * 2.4);
    vec3 q1 = vec3(vUv.x * 7.0, vUv.y * 5.0 - scroll, scroll * 0.5);
    vec3 q2 = vec3(vUv.x * 13.0, vUv.y * 9.0 - scroll * 1.5, scroll * 1.1);
    float n1 = fbm(q1);
    float n2 = fbm(q2);
    float turb = mix(n1, n2, 0.6);

    // Length-based fire color ramp. The hot core ramps to deep red and smoke
    // along the length of the cone. We then radially mix with uOuterColor at
    // the silhouette so the rim reads cool (blue) and center reads hot (yellow).
    vec3 deepRed  = vec3(0.7, 0.06, 0.0);
    vec3 smoke    = vec3(0.04, 0.03, 0.03);

    vec3 hotByLength = uCoreColor;
    hotByLength = mix(hotByLength, uCoreColor * vec3(0.95, 0.7, 0.25), smoothstep(0.10, 0.30, vDist));
    hotByLength = mix(hotByLength, vec3(4.5, 1.6, 0.2), smoothstep(0.30, 0.55, vDist));
    hotByLength = mix(hotByLength, deepRed, smoothstep(0.55, 0.85, vDist));
    hotByLength = mix(hotByLength, smoke,   smoothstep(0.85, 1.00, vDist));

    // Fresnel-style rim term. nv = 1 means surface faces camera (center column
    // of the cone); nv = 0 means surface is grazing (silhouette).
    vec3 viewDir = normalize(-vViewPos);
    float nv = clamp(abs(dot(vViewNormal, viewDir)), 0.0, 1.0);
    // Sharpen so the cool band is a thin rim, not the whole cone.
    float rim = pow(1.0 - nv, 1.7);

    // The blue/cyan envelope is strongest at the throat; it cools to smoke as
    // the plume trails back so the tail doesn't read as a glowing rim.
    float rimEnergy = rim * (1.0 - smoothstep(0.55, 1.0, vDist));
    vec3 col = hotByLength + uOuterColor * rimEnergy * 1.4;

    // Radial mask + soft edge feather (extra power-curve so falloff feels
    // sub-pixel rather than a hard line).
    float radial = abs(vUv.x - 0.5) * 2.0;
    float coreMask = pow(1.0 - smoothstep(0.0, 0.95, radial), 1.3);
    float edgeFeather = pow(1.0 - smoothstep(0.55, 1.05, radial), 1.6);

    // Alpha carving: throat solid, tail breaks up via fbm.
    float carveAmount = mix(0.18, 1.5, vDist);
    float carved = clamp(turb * carveAmount + 0.55, 0.0, 1.6);

    // uExtension controls the visible cone length. At idle (~0.18), the plume
    // fades by ~vDist 0.18; at full (1.0), it extends to the geometry tip.
    float ext = clamp(uExtension, 0.05, 1.0);
    float fadeStart = ext * 0.55;
    float fadeEnd   = ext * 1.0;
    float lengthFade = 1.0 - smoothstep(fadeStart, fadeEnd, vDist);
    // Soften with extra power so the tail dissolves rather than terminating.
    lengthFade = pow(clamp(lengthFade, 0.0, 1.0), 1.4);

    float baseFade = smoothstep(0.0, 0.03, vDist);

    float alpha = coreMask * carved * lengthFade * baseFade * edgeFeather;
    alpha = clamp(alpha * (0.6 + uIntensity * 0.7), 0.0, 1.0);

    // Subtle flicker so brightness doesn't read as a constant lamp.
    float flicker = 0.92 + 0.08 * fbm(vec3(scroll * 0.5, vDist * 3.0, 0.0));
    col *= flicker * (0.45 + uIntensity * 0.85);

    // Keep the cone below the global bloom threshold (~1.5 in linear HDR) so
    // the post-FX bloom pass doesn't grab the exhaust. Additive blending still
    // sells the glow — the per-pixel value just doesn't bleed.
    col = min(col, vec3(1.4));

    gl_FragColor = vec4(col, alpha);
  }
`;

const engineHashNode = makeWgslFn(`
fn engine_hash(p: vec3<f32>) -> f32 {
  var q = fract(p * 0.3183099 + vec3<f32>(0.1, 0.2, 0.3));
  q = q * 17.0;
  return fract(q.x * q.y * q.z * (q.x + q.y + q.z));
}
`);

const engineNoiseNode = makeWgslFn(`
fn engine_noise(p: vec3<f32>) -> f32 {
  let i = floor(p);
  let f0 = fract(p);
  let f = f0 * f0 * (vec3<f32>(3.0, 3.0, 3.0) - 2.0 * f0);
  let n000 = engine_hash(i + vec3<f32>(0.0, 0.0, 0.0));
  let n100 = engine_hash(i + vec3<f32>(1.0, 0.0, 0.0));
  let n010 = engine_hash(i + vec3<f32>(0.0, 1.0, 0.0));
  let n110 = engine_hash(i + vec3<f32>(1.0, 1.0, 0.0));
  let n001 = engine_hash(i + vec3<f32>(0.0, 0.0, 1.0));
  let n101 = engine_hash(i + vec3<f32>(1.0, 0.0, 1.0));
  let n011 = engine_hash(i + vec3<f32>(0.0, 1.0, 1.0));
  let n111 = engine_hash(i + vec3<f32>(1.0, 1.0, 1.0));
  let nx00 = mix(n000, n100, f.x);
  let nx10 = mix(n010, n110, f.x);
  let nx01 = mix(n001, n101, f.x);
  let nx11 = mix(n011, n111, f.x);
  let nxy0 = mix(nx00, nx10, f.y);
  let nxy1 = mix(nx01, nx11, f.y);
  return mix(nxy0, nxy1, f.z);
}
`, [engineHashNode]);

const engineFbmNode = makeWgslFn(`
fn engine_fbm(p: vec3<f32>) -> f32 {
  var q = p;
  var v = 0.0;
  var a = 0.5;
  for (var i = 0; i < 4; i = i + 1) {
    v = v + a * engine_noise(q);
    q = q * 2.18;
    a = a * 0.5;
  }
  return v;
}
`, [engineNoiseNode]);

/**
 * Volumetric hot-core cone for engine exhaust. HDR colors > 1.0 so the surface
 * saturates to white at the throat and bloom catches the cyan rim.
 *
 * Geometry orientation: wide base at the local origin (nozzle), narrow tip
 * trails back along local -Z.
 */
const engineCoreNode = makeWgslFn(`
fn engine_core(
  texCoord: vec2<f32>,
  viewNormal: vec3<f32>,
  viewPosition: vec3<f32>,
  time: f32,
  intensity: f32,
  extension: f32,
  coreColor: vec3<f32>,
  outerColor: vec3<f32>
) -> vec4<f32> {
  if (intensity <= 0.001) {
    return vec4<f32>(0.0, 0.0, 0.0, 0.0);
  }

  let scroll = time * (1.8 + intensity * 2.4);
  let n1 = engine_fbm(vec3<f32>(texCoord.x * 7.0, texCoord.y * 5.0 - scroll, scroll * 0.5));
  let n2 = engine_fbm(vec3<f32>(texCoord.x * 13.0, texCoord.y * 9.0 - scroll * 1.5, scroll * 1.1));
  let turb = mix(n1, n2, 0.6);

  let deepRed = vec3<f32>(0.7, 0.06, 0.0);
  let smoke = vec3<f32>(0.04, 0.03, 0.03);
  let dist = texCoord.y;

  var hotByLength = coreColor;
  hotByLength = mix(hotByLength, coreColor * vec3<f32>(0.95, 0.7, 0.25), smoothstep(0.10, 0.30, dist));
  hotByLength = mix(hotByLength, vec3<f32>(4.5, 1.6, 0.2), smoothstep(0.30, 0.55, dist));
  hotByLength = mix(hotByLength, deepRed, smoothstep(0.55, 0.85, dist));
  hotByLength = mix(hotByLength, smoke, smoothstep(0.85, 1.00, dist));

  let viewDir = normalize(-viewPosition);
  let nv = clamp(abs(dot(normalize(viewNormal), viewDir)), 0.0, 1.0);
  let rim = pow(1.0 - nv, 1.7);
  let rimEnergy = rim * (1.0 - smoothstep(0.55, 1.0, dist));
  var col = hotByLength + outerColor * rimEnergy * 1.4;

  let radial = abs(texCoord.x - 0.5) * 2.0;
  let coreMask = pow(1.0 - smoothstep(0.0, 0.95, radial), 1.3);
  let edgeFeather = pow(1.0 - smoothstep(0.55, 1.05, radial), 1.6);
  let carveAmount = mix(0.18, 1.5, dist);
  let carved = clamp(turb * carveAmount + 0.55, 0.0, 1.6);
  let ext = clamp(extension, 0.05, 1.0);
  let fadeStart = ext * 0.55;
  let fadeEnd = ext;
  var lengthFade = 1.0 - smoothstep(fadeStart, fadeEnd, dist);
  lengthFade = pow(clamp(lengthFade, 0.0, 1.0), 1.4);
  let baseFade = smoothstep(0.0, 0.03, dist);
  var alpha = coreMask * carved * lengthFade * baseFade * edgeFeather;
  alpha = clamp(alpha * (0.6 + intensity * 0.7), 0.0, 1.0);

  let flicker = 0.92 + 0.08 * engine_fbm(vec3<f32>(scroll * 0.5, dist * 3.0, 0.0));
  col = min(col * flicker * (0.45 + intensity * 0.85), vec3<f32>(1.4, 1.4, 1.4));
  return vec4<f32>(col, alpha);
}
`, [engineFbmNode]);

function makeWebGPUEngineCoreMaterial(): MeshBasicNodeMaterial {
  const uniforms: WebGPUEngineCoreUniforms = {
    uTime: makeNodeUniform(0),
    uIntensity: makeNodeUniform(0),
    uExtension: makeNodeUniform(0.18),
    uCoreColor: makeNodeUniform(new THREE.Vector3(8.0, 6.5, 3.0)),
    uOuterColor: makeNodeUniform(new THREE.Vector3(0.6, 1.6, 4.5)),
  };
  const material = new MeshBasicNodeMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    toneMapped: false,
  });
  const core = engineCoreNode({
    texCoord: makeNodeUv(),
    viewNormal: normalView,
    viewPosition: positionView,
    time: uniforms.uTime,
    intensity: uniforms.uIntensity,
    extension: uniforms.uExtension,
    coreColor: uniforms.uCoreColor,
    outerColor: uniforms.uOuterColor,
  });
  material.colorNode = core.rgb;
  material.opacityNode = core.a;
  material.userData.webgpuEngineCoreUniforms = uniforms;
  return material;
}

export function createEngineCore(options: EngineCoreOptions = {}): EngineCoreHandle {
  const length = options.length ?? 2.0;
  const radius = options.radius ?? 0.22;
  const segments = options.segments ?? 28;

  const geometry = new THREE.ConeGeometry(radius, length, segments, 6, true);
  geometry.translate(0, length / 2, 0);
  geometry.rotateX(-Math.PI / 2);

  const material = options.rendererMode === 'webgpu'
    ? makeWebGPUEngineCoreMaterial()
    : new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uIntensity: { value: 0 },
        uExtension: { value: 0.18 },
        uCoreColor: { value: new THREE.Vector3(8.0, 6.5, 3.0) },
        uOuterColor: { value: new THREE.Vector3(0.6, 1.6, 4.5) },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.frustumCulled = false;
  mesh.renderOrder = 6;

  let intensity = 0;
  let extension = 0.18;

  return {
    mesh,
    material,
    setIntensity(value: number): void {
      intensity = THREE.MathUtils.clamp(value, 0, 4);
    },
    setExtension(value: number): void {
      extension = THREE.MathUtils.clamp(value, 0.05, 1.0);
    },
    setCoreColor(color: THREE.Vector3): void {
      if (material instanceof THREE.ShaderMaterial) {
        (material.uniforms.uCoreColor.value as THREE.Vector3).copy(color);
      } else {
        const uniforms = material.userData.webgpuEngineCoreUniforms as WebGPUEngineCoreUniforms;
        uniforms.uCoreColor.value.copy(color);
      }
    },
    setOuterColor(color: THREE.Vector3): void {
      if (material instanceof THREE.ShaderMaterial) {
        (material.uniforms.uOuterColor.value as THREE.Vector3).copy(color);
      } else {
        const uniforms = material.userData.webgpuEngineCoreUniforms as WebGPUEngineCoreUniforms;
        uniforms.uOuterColor.value.copy(color);
      }
    },
    update(dt: number): void {
      const blend = 1 - Math.exp(-12 * dt);
      if (material instanceof THREE.ShaderMaterial) {
        const u = material.uniforms;
        u.uTime.value += dt;
        const currIntensity = u.uIntensity.value as number;
        u.uIntensity.value = currIntensity + (intensity - currIntensity) * blend;
        const currExt = u.uExtension.value as number;
        u.uExtension.value = currExt + (extension - currExt) * blend;
      } else {
        const uniforms = material.userData.webgpuEngineCoreUniforms as WebGPUEngineCoreUniforms;
        uniforms.uTime.value += dt;
        uniforms.uIntensity.value += (intensity - uniforms.uIntensity.value) * blend;
        uniforms.uExtension.value += (extension - uniforms.uExtension.value) * blend;
      }
    },
    dispose(): void {
      geometry.dispose();
      material.dispose();
    },
  };
}

/**
 * Convert a hue angle (degrees) to an HDR Vector3 for the core/outer color
 * uniforms. Values are pushed past 1.0 so saturation + bloom land properly.
 */
export function hueToCoreHdr(hueDeg: number, hot = true): THREE.Vector3 {
  const h = THREE.MathUtils.euclideanModulo(hueDeg, 360) / 360;
  const c = new THREE.Color().setHSL(h, hot ? 1.0 : 0.85, hot ? 0.62 : 0.55);
  const scale = hot ? 8.5 : 4.2;
  return new THREE.Vector3(c.r * scale, c.g * scale, c.b * scale);
}
